import { useMemo, useState } from "react";

import { ChevronRight, Expand, Shrink } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableRow,
} from "~/components/ui/table";
import type { BalanceSheetGroupedData } from "~/types/contabilidad";

interface BalanceSheetTableProps {
  data: BalanceSheetGroupedData | null;
  isLoading: boolean;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function BalanceSheetTable({ data, isLoading }: BalanceSheetTableProps) {
  // Track expanded state for each level
  const [expandedClases, setExpandedClases] = useState<Set<string>>(new Set());
  const [expandedSubClases, setExpandedSubClases] = useState<Set<string>>(
    new Set()
  );
  const [expandedSubClases2, setExpandedSubClases2] = useState<Set<string>>(
    new Set()
  );
  const [expandedGrupos, setExpandedGrupos] = useState<Set<string>>(new Set());

  // Calculate all possible keys for expand/collapse all
  const allKeys = useMemo(() => {
    if (!data) return { clases: [], subClases: [], subClases2: [], grupos: [] };

    const clases: string[] = [];
    const subClases: string[] = [];
    const subClases2: string[] = [];
    const grupos: string[] = [];

    data.clases.forEach((clase) => {
      clases.push(clase.clase);
      clase.subClases.forEach((sc) => {
        const scKey = `${clase.clase}-${sc.sub_clase}`;
        subClases.push(scKey);
        sc.subClases2.forEach((sc2) => {
          const sc2Key = `${scKey}-${sc2.sub_clase_2}`;
          subClases2.push(sc2Key);
          sc2.grupos.forEach((g) => {
            if (g.grupo_cuenta_name) {
              grupos.push(`${sc2Key}-${g.grupo_cuenta_id ?? "ungrouped"}`);
            }
          });
        });
      });
    });

    return { clases, subClases, subClases2, grupos };
  }, [data]);

  const isAllExpanded =
    expandedClases.size === allKeys.clases.length &&
    expandedSubClases.size === allKeys.subClases.length &&
    expandedSubClases2.size === allKeys.subClases2.length &&
    expandedGrupos.size === allKeys.grupos.length;

  const toggleAll = () => {
    if (isAllExpanded) {
      // Collapse all
      setExpandedClases(new Set());
      setExpandedSubClases(new Set());
      setExpandedSubClases2(new Set());
      setExpandedGrupos(new Set());
    } else {
      // Expand all
      setExpandedClases(new Set(allKeys.clases));
      setExpandedSubClases(new Set(allKeys.subClases));
      setExpandedSubClases2(new Set(allKeys.subClases2));
      setExpandedGrupos(new Set(allKeys.grupos));
    }
  };

  const toggleClase = (key: string) => {
    const newSet = new Set(expandedClases);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setExpandedClases(newSet);
  };

  const toggleSubClase = (key: string) => {
    const newSet = new Set(expandedSubClases);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setExpandedSubClases(newSet);
  };

  const toggleSubClase2 = (key: string) => {
    const newSet = new Set(expandedSubClases2);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setExpandedSubClases2(newSet);
  };

  const toggleGrupo = (key: string) => {
    const newSet = new Set(expandedGrupos);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setExpandedGrupos(newSet);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(10)].map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (!data || data.clases.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">
          No hay datos para el periodo seleccionado.
        </p>
        <p className="text-sm text-muted-foreground">
          Verifica que existan cuentas mapeadas en la plantilla seleccionada.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Haz clic en las filas para expandir o contraer las secciones
        </p>
        <Button variant="outline" size="sm" onClick={toggleAll}>
          {isAllExpanded ? (
            <>
              <Shrink className="mr-2 h-4 w-4" />
              Contraer Todo
            </>
          ) : (
            <>
              <Expand className="mr-2 h-4 w-4" />
              Expandir Todo
            </>
          )}
        </Button>
      </div>

      <div className="rounded-xl border">
        <Table>
          <TableBody>
            {data.clases.map((clase, index) => {
              const claseKey = clase.clase;
              const isClaseExpanded = expandedClases.has(claseKey);

              return (
                <>
                  {/* CLASE header - Level 1 (clickable) */}
                  <TableRow
                    key={`clase-${claseKey}`}
                    className="cursor-pointer bg-muted/60 duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted"
                    style={{
                      animationDelay: `${Math.min(index * 40, 400)}ms`,
                    }}
                    onClick={() => toggleClase(claseKey)}
                  >
                    <TableCell className="py-3 text-base font-bold tracking-wide">
                      <div className="flex items-center gap-2">
                        <ChevronRight
                          className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                            isClaseExpanded ? "rotate-90" : ""
                          }`}
                        />
                        {clase.clase.toUpperCase()}
                      </div>
                    </TableCell>
                    <TableCell className="w-[150px] text-right font-mono text-base font-bold">
                      {formatCurrency(clase.total)}
                    </TableCell>
                  </TableRow>

                  {isClaseExpanded &&
                    clase.subClases.map((subClase) => {
                      const subClaseKey = `${claseKey}-${subClase.sub_clase}`;
                      const isSubClaseExpanded =
                        expandedSubClases.has(subClaseKey);

                      return (
                        <>
                          {/* SUB_CLASE header - Level 2 (clickable) */}
                          <TableRow
                            key={`subclase-${subClaseKey}`}
                            className="cursor-pointer bg-muted/30 duration-200 animate-in fade-in-0 slide-in-from-top-1 hover:bg-muted/50"
                            onClick={() => toggleSubClase(subClaseKey)}
                          >
                            <TableCell className="py-2.5 pl-6 font-semibold">
                              <div className="flex items-center gap-2">
                                <ChevronRight
                                  className={`h-4 w-4 transition-transform duration-200 ${
                                    isSubClaseExpanded ? "rotate-90" : ""
                                  }`}
                                />
                                {subClase.sub_clase}
                              </div>
                            </TableCell>
                            <TableCell className="w-[150px] text-right font-mono font-semibold">
                              {formatCurrency(subClase.total)}
                            </TableCell>
                          </TableRow>

                          {isSubClaseExpanded &&
                            subClase.subClases2.map((subClase2) => {
                              const subClase2Key = `${subClaseKey}-${subClase2.sub_clase_2}`;
                              const isSubClase2Expanded =
                                expandedSubClases2.has(subClase2Key);

                              return (
                                <>
                                  {/* SUB_CLASE_2 header - Level 3 (clickable) */}
                                  <TableRow
                                    key={`subclase2-${subClase2Key}`}
                                    className="cursor-pointer duration-200 animate-in fade-in-0 slide-in-from-top-1 hover:bg-muted/40"
                                    onClick={() =>
                                      toggleSubClase2(subClase2Key)
                                    }
                                  >
                                    <TableCell className="py-2 pl-10 font-medium">
                                      <div className="flex items-center gap-2">
                                        <ChevronRight
                                          className={`h-4 w-4 transition-transform duration-200 ${
                                            isSubClase2Expanded
                                              ? "rotate-90"
                                              : ""
                                          }`}
                                        />
                                        {subClase2.sub_clase_2}
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[150px] text-right font-mono font-medium">
                                      {formatCurrency(subClase2.total)}
                                    </TableCell>
                                  </TableRow>

                                  {isSubClase2Expanded &&
                                    subClase2.grupos.map(
                                      (grupo, grupoIndex) => {
                                        const grupoKey = `${subClase2Key}-${grupo.grupo_cuenta_id ?? "ungrouped"}`;
                                        const isGrupoExpanded =
                                          expandedGrupos.has(grupoKey);
                                        const hasGrupoHeader =
                                          !!grupo.grupo_cuenta_name;
                                        const showAccounts =
                                          !hasGrupoHeader || isGrupoExpanded;

                                        return (
                                          <>
                                            {/* GRUPO header - Level 4 (clickable when grupo has a name) */}
                                            {hasGrupoHeader && (
                                              <TableRow
                                                key={`grupo-header-${subClase2Key}-${grupoIndex}`}
                                                className="cursor-pointer duration-200 animate-in fade-in-0 slide-in-from-top-1 hover:bg-muted/40"
                                                onClick={() =>
                                                  toggleGrupo(grupoKey)
                                                }
                                              >
                                                <TableCell className="py-1.5 pl-14 text-sm font-medium text-muted-foreground">
                                                  <div className="flex items-center gap-2">
                                                    <ChevronRight
                                                      className={`h-3.5 w-3.5 transition-transform duration-200 ${
                                                        isGrupoExpanded
                                                          ? "rotate-90"
                                                          : ""
                                                      }`}
                                                    />
                                                    {grupo.grupo_cuenta_code
                                                      ? `${grupo.grupo_cuenta_code} - `
                                                      : ""}
                                                    {grupo.grupo_cuenta_name}
                                                  </div>
                                                </TableCell>
                                                <TableCell className="w-[150px] text-right font-mono text-sm font-medium text-muted-foreground">
                                                  {formatCurrency(grupo.total)}
                                                </TableCell>
                                              </TableRow>
                                            )}

                                            {/* Individual accounts - Level 5 */}
                                            {showAccounts &&
                                              grupo.accounts.map((account) => (
                                                <TableRow
                                                  key={`account-${account.account_id}`}
                                                  className="duration-200 animate-in fade-in-0 slide-in-from-top-1 hover:bg-muted/30"
                                                >
                                                  <TableCell
                                                    className={`py-1.5 ${
                                                      hasGrupoHeader
                                                        ? "pl-[72px]"
                                                        : "pl-14"
                                                    }`}
                                                  >
                                                    <span className="font-mono text-sm text-muted-foreground">
                                                      {account.account_code}
                                                    </span>
                                                    <span className="ml-3 text-sm">
                                                      {account.account_name}
                                                    </span>
                                                  </TableCell>
                                                  <TableCell className="w-[150px] text-right font-mono text-sm">
                                                    {formatCurrency(
                                                      account.saldo_final
                                                    )}
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                          </>
                                        );
                                      }
                                    )}
                                </>
                              );
                            })}
                        </>
                      );
                    })}

                  {/* Spacer between clases */}
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={2} className="h-2 bg-background p-0" />
                  </TableRow>
                </>
              );
            })}
          </TableBody>

          {/* Validation footer */}
          <TableFooter>
            <TableRow className="border-t-2 bg-muted/50 hover:bg-muted/50">
              <TableCell className="py-3 font-semibold">
                VALIDACION (Total Activo - Total Pasivo y Patrimonio)
              </TableCell>
              <TableCell
                className={`w-[150px] text-right font-mono text-base font-bold ${
                  Math.abs(data.diferencia) < 1
                    ? "text-green-700"
                    : "text-red-700"
                }`}
              >
                {formatCurrency(data.diferencia)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>

      {Math.abs(data.diferencia) < 1 && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <p className="text-sm font-medium text-green-800">
            El Balance esta cuadrado correctamente.
          </p>
        </div>
      )}
    </div>
  );
}
